/**
 * @module fx/Particles
 * Juice particle system — emits coloured splatter particles on fruit slash.
 *
 * Each fruit type has an associated juice colour (defined in game-config).
 * Particles are short-lived physics objects: velocity + gravity, alpha fade.
 */

/** A single particle in the system. */
export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Remaining lifetime [0–1], decreases each frame. */
  life: number;
  /** CSS hex colour string (e.g. "#ff69b4" for strawberry). */
  color: string;
  radius: number;
}

/** Emits and updates juice splatter particles. */
export class ParticleSystem {
  private _particles: Particle[] = [];

  /**
   * Emits a burst of particles at the given position.
   *
   * @param x - Spawn X (canvas pixels).
   * @param y - Spawn Y (canvas pixels).
   * @param color - Juice colour for this fruit type.
   * @param count - Number of particles to emit.
   */
  emit(x: number, y: number, color: string, count = 12): void {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 100 + Math.random() * 300;
      this._particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        color,
        radius: 3 + Math.random() * 5,
      });
    }
  }

  /**
   * Advances all particles by one frame.
   * Dead particles (life <= 0) are removed.
   *
   * @param deltaMs - Frame delta in milliseconds.
   * @param gravity - Downward acceleration (px/s²) from CONFIG.
   */
  update(deltaMs: number, gravity: number): void {
    const dt = deltaMs / 1000;
    for (const p of this._particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += gravity * dt;
      p.life -= dt * 2; // fade over ~0.5 s
    }
    this._particles = this._particles.filter((p) => p.life > 0);
  }

  /** Live particles for the render layer. */
  get particles(): readonly Particle[] {
    return this._particles;
  }

  /** Removes all particles (e.g. on game reset). */
  clear(): void {
    this._particles = [];
  }
}
